import { z } from "zod";

import {
  DELIVERY_STATUSES,
  DMARC_RESULTS,
  LIMITS,
  MESSAGE_SORT_FIELDS,
  SPAM_VERDICTS,
  SYSTEM_FOLDERS,
} from "../constants.js";
import { attachmentSchema } from "./attachment.js";
import {
  addressObjectSchema,
  attachmentIdSchema,
  booleanQuerySchema,
  draftIdSchema,
  emailAddressSchema,
  isoUtcSchema,
  labelIdSchema,
  listQuerySchema,
  mailboxIdSchema,
  messageIdSchema,
  threadIdSchema,
} from "./common.js";

export const systemFolderSchema = z.enum([...SYSTEM_FOLDERS]);
export const dmarcResultSchema = z.enum([...DMARC_RESULTS]);
export const spamVerdictSchema = z.enum([...SPAM_VERDICTS]);
export const deliveryStatusSchema = z.enum([...DELIVERY_STATUSES]);

/** SPF/DKIM/DMARC evaluation results attached to an inbound message. */
export const authResultsSchema = z.object({
  spf: z.enum(["pass", "fail", "softfail", "neutral", "none", "temperror", "permerror"]),
  dkim: z.enum(["pass", "fail", "none", "temperror", "permerror"]),
  dmarc: dmarcResultSchema,
  dmarcPolicy: z.enum(["none", "quarantine", "reject"]).nullable(),
});
export type AuthResults = z.infer<typeof authResultsSchema>;

/* -------------------------------------------------------------------------- */
/* Message resources                                                           */
/* -------------------------------------------------------------------------- */

export const messageSummarySchema = z.object({
  id: messageIdSchema,
  threadId: threadIdSchema,
  mailboxId: mailboxIdSchema,
  subject: z.string().max(LIMITS.maxSubjectLength),
  snippet: z.string().max(LIMITS.maxSnippetLength),
  from: addressObjectSchema,
  to: z.array(addressObjectSchema).max(LIMITS.maxRecipientsPerMessage),
  receivedAt: isoUtcSchema,
  sentAt: isoUtcSchema.nullable(),
  folder: systemFolderSchema,
  isRead: z.boolean(),
  isFlagged: z.boolean(),
  hasAttachments: z.boolean(),
  attachmentCount: z.number().int().nonnegative(),
  labelIds: z.array(labelIdSchema),
  sizeBytes: z.number().int().nonnegative(),
  spamVerdict: spamVerdictSchema,
});
export type MessageSummary = z.infer<typeof messageSummarySchema>;

/** Sanitized body. `html` is always sanitized before it is stored or served. */
export const messageBodySchema = z.object({
  text: z.string(),
  html: z.string().nullable(),
  sanitizedAt: isoUtcSchema,
});
export type MessageBody = z.infer<typeof messageBodySchema>;

export const messageDetailSchema = messageSummarySchema.extend({
  cc: z.array(addressObjectSchema).max(LIMITS.maxRecipientsPerMessage),
  bcc: z.array(addressObjectSchema).max(LIMITS.maxRecipientsPerMessage),
  replyTo: addressObjectSchema.nullable(),
  body: messageBodySchema,
  attachments: z.array(attachmentSchema),
  auth: authResultsSchema.nullable(),
  messageIdHeader: z.string().max(998).nullable(),
  inReplyTo: z.string().max(998).nullable(),
  references: z.array(z.string().max(998)),
  listUnsubscribe: z.string().max(4096).nullable(),
  autoSubmitted: z.string().max(64).nullable(),
  rawAvailable: z.boolean(),
});
export type MessageDetail = z.infer<typeof messageDetailSchema>;

/* -------------------------------------------------------------------------- */
/* Queries                                                                     */
/* -------------------------------------------------------------------------- */

export const listMessagesQuerySchema = listQuerySchema.extend({
  folder: systemFolderSchema.optional(),
  labelId: labelIdSchema.optional(),
  isRead: booleanQuerySchema.optional(),
  isFlagged: booleanQuerySchema.optional(),
  hasAttachment: booleanQuerySchema.optional(),
  from: emailAddressSchema.optional(),
  to: emailAddressSchema.optional(),
  subject: z.string().max(256).optional(),
  before: isoUtcSchema.optional(),
  after: isoUtcSchema.optional(),
  sortBy: z.enum([...MESSAGE_SORT_FIELDS]).default("received_at"),
});
export type ListMessagesQuery = z.infer<typeof listMessagesQuerySchema>;

export const listMessagesResponseSchema = z.object({
  messages: z.array(messageSummarySchema),
  pageInfo: z.object({
    limit: z.number().int().positive(),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});
export type ListMessagesResponse = z.infer<typeof listMessagesResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Mutations                                                                   */
/* -------------------------------------------------------------------------- */

export const updateMessageRequestSchema = z.object({
  isRead: z.boolean().optional(),
  isFlagged: z.boolean().optional(),
  folder: systemFolderSchema.optional(),
});
export type UpdateMessageRequest = z.infer<typeof updateMessageRequestSchema>;

export const BULK_ACTIONS = [
  "mark_read",
  "mark_unread",
  "flag",
  "unflag",
  "archive",
  "unarchive",
  "move_trash",
  "untrash",
  "move_spam",
  "unspam",
  "add_label",
  "remove_label",
  "delete_permanent",
] as const;
export const bulkActionSchema = z.enum([...BULK_ACTIONS]);
export type BulkAction = z.infer<typeof bulkActionSchema>;

export const bulkMessageActionRequestSchema = z
  .object({
    ids: z.array(messageIdSchema).min(1).max(500),
    action: bulkActionSchema,
    labelId: labelIdSchema.optional(),
  })
  .refine(
    (value) =>
      !(value.action === "add_label" || value.action === "remove_label") ||
      value.labelId !== undefined,
    { message: "labelId is required for label actions", path: ["labelId"] },
  );
export type BulkMessageActionRequest = z.infer<typeof bulkMessageActionRequestSchema>;

export const bulkMessageActionResponseSchema = z.object({
  ok: z.literal(true),
  affected: z.number().int().nonnegative(),
});

/* -------------------------------------------------------------------------- */
/* Outbound compose                                                            */
/* -------------------------------------------------------------------------- */

const recipientsSchema = z.array(addressObjectSchema).max(LIMITS.maxRecipientsPerMessage);

export const sendMessageRequestSchema = z.object({
  mailboxId: mailboxIdSchema,
  to: recipientsSchema.min(1),
  cc: recipientsSchema.default([]),
  bcc: recipientsSchema.default([]),
  subject: z.string().max(LIMITS.maxSubjectLength).default(""),
  text: z.string().max(LIMITS.maxTextBodyBytes).optional(),
  html: z.string().max(LIMITS.maxHtmlBodyBytes).optional(),
  attachmentIds: z.array(attachmentIdSchema).max(LIMITS.maxAttachmentsPerMessage).default([]),
  inReplyTo: z.string().max(998).optional(),
  references: z.array(z.string().max(998)).max(100).default([]),
  draftId: draftIdSchema.optional(),
});
export type SendMessageRequest = z.infer<typeof sendMessageRequestSchema>;

export const sendMessageResponseSchema = z.object({
  messageId: messageIdSchema,
  threadId: threadIdSchema,
  status: deliveryStatusSchema,
});
export type SendMessageResponse = z.infer<typeof sendMessageResponseSchema>;

export const replyMessageRequestSchema = z.object({
  replyAll: z.boolean().default(false),
  text: z.string().max(LIMITS.maxTextBodyBytes).optional(),
  html: z.string().max(LIMITS.maxHtmlBodyBytes).optional(),
  attachmentIds: z.array(attachmentIdSchema).max(LIMITS.maxAttachmentsPerMessage).default([]),
});
export type ReplyMessageRequest = z.infer<typeof replyMessageRequestSchema>;

export const forwardMessageRequestSchema = z.object({
  to: recipientsSchema.min(1),
  cc: recipientsSchema.default([]),
  bcc: recipientsSchema.default([]),
  text: z.string().max(LIMITS.maxTextBodyBytes).optional(),
  html: z.string().max(LIMITS.maxHtmlBodyBytes).optional(),
  attachmentIds: z.array(attachmentIdSchema).max(LIMITS.maxAttachmentsPerMessage).default([]),
});
export type ForwardMessageRequest = z.infer<typeof forwardMessageRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Threads                                                                     */
/* -------------------------------------------------------------------------- */

export const threadSummarySchema = z.object({
  id: threadIdSchema,
  mailboxId: mailboxIdSchema,
  subject: z.string(),
  messageIds: z.array(messageIdSchema),
  latestReceivedAt: isoUtcSchema,
  unreadCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  participantAddresses: z.array(emailAddressSchema),
});
export type ThreadSummary = z.infer<typeof threadSummarySchema>;

export const threadDetailSchema = threadSummarySchema.extend({
  messages: z.array(messageDetailSchema),
});
export type ThreadDetail = z.infer<typeof threadDetailSchema>;
